import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import AsyncSelect from 'react-select/async';
import { useDebouncedEffect } from '../../hooks/useDebounceEffect';
import { lookupOptions } from './hooks/useOptionsLookup';
import { useSideChannelSubscription } from '../../util/useSideChannel';
import getPathFromId from '../../util/getPathFromId';
import { useJnx } from '../../util/jnx';
import { Trans } from '@lingui/react/macro';
import useTestId from '../../hooks/useTestId';

function Autocomplete(props) {
  const {
    schema: { title, lookup },
    registry,
    value,
    formData,
    onChange,
    disabled,
    required,
    fieldPathId: { $id, path } = {},
    uiSchema: { 'ui:readonly': uiReadonly },
    readonly: propReadonly,
  } = props;
  const formContext = registry?.formContext;
  const { sideChannel } = formContext;

  const { onSelectExpr, id, label: labelExpr, searchParam } = lookup || {};

  const promiseRef = useRef();
  const testIdProps = useTestId($id);

  const rootFormData = useSideChannelSubscription(sideChannel, 0);

  // const path = useMemo(() => getPathFromId($id), [$id]);

  const labelJnx = useJnx(labelExpr);

  const onSelectJnx = useJnx(onSelectExpr);
  const label = useMemo(
    () =>
      onSelectJnx?.eval(rootFormData || {}, path, {
        formData,
        rootFormData,
      }),
    [onSelectJnx, formData, rootFormData, path]
  );

  const [current, setCurrent] = useState(value);
  useDebouncedEffect(
    () => {
      if (current !== value) {
        onChange(current.value, path);
      }
    },
    [current],
    450
  );

  useEffect(() => setCurrent(value), [value]);

  const readonly = uiReadonly || propReadonly;

  const promiseOptions = useCallback(
    (inputValue, isQR = false) => {
      const searchValue = inputValue.trim();

      if (searchValue.length > 2) {
        const search = { searchParam };
        search.promise = new Promise(resolve => {
          search.resolve = resolve;
        });
        promiseRef.current = search.promise;
        setTimeout(async () => {
          if (promiseRef.current === search.promise) {
            const options = await lookupOptions(
              { ...lookup, params: { ...lookup.params, [searchParam]: searchValue } },
              rootFormData,
              formContext,
              path,
            );

            if (isQR) {
              const obj = options?.[0];
              setCurrent({ value: obj[id], label: labelJnx.eval(obj) });
              search.resolve([
                {
                  value: obj[id],
                  label: labelJnx.eval(obj),
                },
              ]);
            } else {
              const dataParse = (options || []).map(obj => {
                return {
                  value: obj[id],
                  label: labelJnx.eval(obj),
                };
              });
              search.resolve(dataParse);
            }
          }
        }, 300);

        return search.promise;
      }

      // react-select's async loader needs a promise, or the menu stays on "Loading..." for short input.
      return Promise.resolve([]);
    },
    [rootFormData, formContext, searchParam, lookupOptions, id, labelJnx]
  );

  useEffect(() => {
    if (label) {
      promiseOptions(label, true);
    }
  }, [label, onSelectJnx, promiseOptions]);

  return (
    <div>
      {title.trim() ? (
        <label className="control-label">
          {title}
          {required ? <span className="required">*</span> : null}
        </label>
      ) : null}
      <AsyncSelect
        inputId={$id}
        {...testIdProps}
        className="autocomplete-container"
        classNamePrefix="autocomplete"
        menuPortalTarget={document.body}
        styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
        loadingMessage={() => <Trans>Loading...</Trans>}
        noOptionsMessage={({ inputValue }) => (inputValue.length > 2 ? <Trans>No matching results</Trans> : <Trans>Type to search</Trans>)}
        required={required}
        isDisabled={readonly || disabled}
        cacheOptions
        defaultOptions
        value={current}
        // react-select passes a callback as the 2nd argument; keep it out of promiseOptions' `isQR` flag.
        loadOptions={inputValue => promiseOptions(inputValue)}
        onChange={value => setCurrent(value)}
      />
    </div>
  );
}

export default Autocomplete;
